import { Account } from '@core/model/domain/account';
import { Bank } from '@core/model/domain/bank';
import {
  ConnectionFormItemDto,
  RegionalBankPayload,
} from '@core/service/digital-factory/bank/types/dto/connectionFormDto';

export enum ProcessEventType {
  CONSENT,
  BANK_CHOICE,
  GO_BACK_TO_BANK_CHOICE,
  ACCOUNTS_FOUND,
  CONTINUE_AFTER_ERROR,
  ABANDON,
  CONTINUE,
  INTERNAL_ERROR,
  WINDOW_RESIZE_EVENT,
  ASK_TO_RETRY_WEBAUTH,
  WEBAUTH_WITH_REGIONAL_BANK,
  ACCOUNTS_SELECTED,
  RETRY_ACCOUNT_ANALYSIS,
  CREDENTIALS_AUTHENTICATION,
  RETRY_BANK_AUTHENTICATION,
}
export interface ConsentEvent {
  type: ProcessEventType.CONSENT;
}

export interface BankChoiceEvent {
  type: ProcessEventType.BANK_CHOICE;
  data: Bank;
}

export interface GoBackToBankChoiceEvent {
  type: ProcessEventType.GO_BACK_TO_BANK_CHOICE;
}

export interface AccountsFoundEvent {
  type: ProcessEventType.ACCOUNTS_FOUND;
  data: Account[];
}

export interface ContinueAfterErrorEvent {
  type: ProcessEventType.CONTINUE_AFTER_ERROR;
}
export interface AbandonEvent {
  type: ProcessEventType.ABANDON;
}

export interface AccountsSelectedEvent {
  type: ProcessEventType.ACCOUNTS_SELECTED;
  selectedAccounts: Account[];
}

export interface ContinueEvent {
  type: ProcessEventType.CONTINUE;
}

export interface InternalErrorEvent {
  type: ProcessEventType.INTERNAL_ERROR;
  payload: {
    errorOrigin: string;
    error: unknown;
  };
}

export interface WindowResizeEvent {
  type: ProcessEventType.WINDOW_RESIZE_EVENT;
  data: string;
}

export interface AskToRetryWebauth {
  type: ProcessEventType.ASK_TO_RETRY_WEBAUTH;
}

export interface WebauthWithRegionalBank {
  type: ProcessEventType.WEBAUTH_WITH_REGIONAL_BANK;
  regionalBankPayload: RegionalBankPayload;
}

export interface RetryAccountAnalysisEvent {
  type: ProcessEventType.RETRY_ACCOUNT_ANALYSIS;
}

export interface CredentialsAuthenticationEvent {
  type: ProcessEventType.CREDENTIALS_AUTHENTICATION;
  payload: {
    form: ConnectionFormItemDto[];
    selectedBank: Bank;
  };
}

export interface RetryBankAuthentication {
  type: ProcessEventType.RETRY_BANK_AUTHENTICATION;
}

export type AcceptedProcessEvent =
  | ConsentEvent
  | BankChoiceEvent
  | GoBackToBankChoiceEvent
  | AccountsFoundEvent
  | AccountsSelectedEvent
  | AskToRetryWebauth
  | RetryAccountAnalysisEvent
  | WebauthWithRegionalBank
  | CredentialsAuthenticationEvent
  | RetryBankAuthentication;

export type ParentProcessEvent =
  | AbandonEvent
  | ContinueAfterErrorEvent
  | ContinueEvent
  | InternalErrorEvent
  | WindowResizeEvent;

export class ProcessEventFactory {
  public static SpawnConsentEvent(): ConsentEvent {
    return { type: ProcessEventType.CONSENT };
  }

  public static SpawnBankChoiceEvent(bank: Bank): BankChoiceEvent {
    return { type: ProcessEventType.BANK_CHOICE, data: bank };
  }

  public static SpawnGoBackToBankChoiceEvent(): GoBackToBankChoiceEvent {
    return { type: ProcessEventType.GO_BACK_TO_BANK_CHOICE };
  }

  public static SpawnAccountsFoundEvent(accounts: Account[]): AccountsFoundEvent {
    return { type: ProcessEventType.ACCOUNTS_FOUND, data: accounts };
  }

  public static SpawnContinueAfterErrorEvent(): ContinueAfterErrorEvent {
    return { type: ProcessEventType.CONTINUE_AFTER_ERROR };
  }

  public static SpawnAbandonEvent(): AbandonEvent {
    return { type: ProcessEventType.ABANDON };
  }

  public static SpawnAccountsSelectedEvent(payload: { selectedAccounts: Account[] }): AccountsSelectedEvent {
    return { type: ProcessEventType.ACCOUNTS_SELECTED, selectedAccounts: payload.selectedAccounts };
  }

  public static SpawnContinueEvent(): ContinueEvent {
    return { type: ProcessEventType.CONTINUE };
  }

  public static SpawnInternalErrorEvent(payload: {
    errorOrigin: string;
    error: unknown;
  }): InternalErrorEvent {
    return { type: ProcessEventType.INTERNAL_ERROR, payload };
  }

  public static SpawnWindowResizeEvent(size: string): WindowResizeEvent {
    return { type: ProcessEventType.WINDOW_RESIZE_EVENT, data: size };
  }

  public static SpawnAskToRetryWebauth(): AskToRetryWebauth {
    return { type: ProcessEventType.ASK_TO_RETRY_WEBAUTH };
  }

  public static SpawnWebauthWithRegionalBank(
    regionalBankPayload: RegionalBankPayload
  ): WebauthWithRegionalBank {
    return { type: ProcessEventType.WEBAUTH_WITH_REGIONAL_BANK, regionalBankPayload };
  }

  public static SpawnCredentialsAuthentication(credentialsAuthenticationPayload: {
    form: ConnectionFormItemDto[];
    selectedBank: Bank;
  }): CredentialsAuthenticationEvent {
    return { type: ProcessEventType.CREDENTIALS_AUTHENTICATION, payload: credentialsAuthenticationPayload };
  }

  public static SpawnRetryAccountAnalysis(): RetryAccountAnalysisEvent {
    return { type: ProcessEventType.RETRY_ACCOUNT_ANALYSIS };
  }

  public static SpawnRetryBankAuthentication(): RetryBankAuthentication {
    return { type: ProcessEventType.RETRY_BANK_AUTHENTICATION };
  }
}
